import React from 'react';
import { Box, ButtonBase, Grid, InputLabel, OutlinedInput, Typography } from '@mui/material';
import { alpha } from '@mui/material/styles';
import EventNoteIcon from '@mui/icons-material/EventNote';
import BoltIcon from '@mui/icons-material/Bolt';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import dayjs from 'dayjs';
import { DateField, FieldError } from '../../../../components/form-page-parts';
import { SUBMISSION_LIMITS, SubmissionType } from '../../../../model/ser/ser-enums';
import { DataStepErrors, SubmissionDraft, isWeekend, weekDayName } from '../submission-send-rules';
import { StepSection } from './step-section';

const TYPE_OPTIONS = [
    { value: SubmissionType.Rotina, title: 'Rotina', description: 'Segue o prazo normal de cada veículo de publicação.', icon: <EventNoteIcon />, color: 'primary' as const },
    { value: SubmissionType.Urgencia, title: 'Urgência', description: 'Sinaliza à diagramação que a publicação é prioritária.', icon: <BoltIcon />, color: 'warning' as const },
];

function TypeCard({ option, selected, onSelect }: { option: typeof TYPE_OPTIONS[number]; selected: boolean; onSelect: () => void }) {
    return (
        <ButtonBase
            role="radio"
            aria-checked={selected}
            onClick={onSelect}
            sx={(theme) => {
                const color = theme.palette[option.color].main;
                return {
                    width: '100%', height: '100%', p: 2, gap: 1.5, borderRadius: 2, textAlign: 'left',
                    display: 'flex', alignItems: 'flex-start', justifyContent: 'flex-start',
                    border: '2px solid', borderColor: selected ? color : 'divider',
                    bgcolor: selected ? alpha(color, 0.06) : 'background.paper',
                    transition: 'border-color 150ms ease-out, background-color 150ms ease-out',
                    '&:hover': { borderColor: selected ? color : alpha(color, 0.5) },
                    '&.Mui-focusVisible': { outline: `2px solid ${color}`, outlineOffset: 2 },
                };
            }}
        >
            <Box sx={{ display: 'flex', p: 1, borderRadius: 1.5, bgcolor: `${option.color}.main`, color: `${option.color}.contrastText` }}>{option.icon}</Box>
            <Box sx={{ flex: 1 }}>
                <Typography fontWeight={700}>{option.title}</Typography>
                <Typography variant="body2" color="text.secondary">{option.description}</Typography>
            </Box>
            <CheckCircleIcon sx={{ color: `${option.color}.main`, opacity: selected ? 1 : 0, transition: 'opacity 150ms ease-out' }} />
        </ButtonBase>
    );
}

export function DataStep({ draft, errors, onChange }: { draft: SubmissionDraft; errors: DataStepErrors; onChange: (patch: Partial<SubmissionDraft>) => void }) {
    const publishDate = draft.publishDate ? dayjs(draft.publishDate) : null;

    return (
        <StepSection title="Dados da remessa" description="Identifique a remessa e informe quando ela deve ser publicada.">
            <Grid container spacing={2.5}>
                <Grid item xs={12} md={8}>
                    <InputLabel required>Título</InputLabel>
                    <OutlinedInput
                        fullWidth
                        autoFocus
                        placeholder="Ex.: Edital de Licitação nº 012/2026"
                        value={draft.title}
                        error={!!errors.title}
                        inputProps={{ maxLength: SUBMISSION_LIMITS.titleMaxLength }}
                        onChange={(e) => onChange({ title: e.target.value })}
                    />
                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                        <FieldError message={errors.title} />
                        <Typography variant="caption" color="text.secondary" sx={{ ml: 'auto', mt: 0.5 }}>{draft.title.length}/{SUBMISSION_LIMITS.titleMaxLength}</Typography>
                    </Box>
                </Grid>
                <Grid item xs={12} md={4}>
                    <DateField
                        label="Data de publicação"
                        required
                        value={draft.publishDate}
                        minDate={dayjs()}
                        shouldDisableDate={isWeekend}
                        error={errors.publishDate}
                        onChange={(value) => onChange({ publishDate: value })}
                    />
                    {publishDate?.isValid() && !errors.publishDate && (
                        <Typography variant="caption" color="text.secondary">Publicação em {weekDayName(publishDate)}</Typography>
                    )}
                </Grid>
                <Grid item xs={12}>
                    <InputLabel required>Tipo</InputLabel>
                    <Grid container spacing={1.5} role="radiogroup" aria-label="Tipo da remessa">
                        {TYPE_OPTIONS.map((option) => (
                            <Grid item xs={12} sm={6} key={option.value}>
                                <TypeCard option={option} selected={draft.type === option.value} onSelect={() => onChange({ type: option.value })} />
                            </Grid>
                        ))}
                    </Grid>
                </Grid>
                <Grid item xs={12}>
                    <InputLabel>Instruções para a diagramação</InputLabel>
                    <OutlinedInput
                        fullWidth
                        multiline
                        minRows={3}
                        placeholder="Ex.: publicar na seção de licitações, manter a tabela do anexo II em página inteira"
                        value={draft.instruction}
                        onChange={(e) => onChange({ instruction: e.target.value })}
                    />
                    <Typography variant="caption" color="text.secondary">Opcional. {draft.instruction.length} caracteres.</Typography>
                </Grid>
            </Grid>
        </StepSection>
    );
}
